import { Injectable } from "@angular/core";
import { Action, Selector, StateContext, Store } from "@ngxs/store";
import { inject } from "@angular/core";
import { State } from "@ngxs/store";
import { CaslPermissionsStateModel, RegisteredAPIModel, RegisteredAPIRoute } from "./casl-permissions.model";
import { CaslPermissionsService } from "../casl-permissions.service";
import { DeleteRoutePermission, GetRegisteredAPIModels, GetRegisteredAPIRoutes, GetRoleDropdownOptions, GetRoutePermissions, SetCaslPermissionType, SetSelectedRoutePermission, SubmitRoutePermission } from "./casl-permissions.actions";
import { catchError, tap } from "rxjs";
import { OnCanCloseDialog, SetShowLoader, ShowToastrAlert } from "app/modules/shared-state/shared.actions";
import { OnSetSharedGridRowData } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions";
import { SharedGridState } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.state";
import { DropdownOptionsService } from "app/modules/system-setup/dropdown-options/dropdown-options.service";


@State<CaslPermissionsStateModel>({
    name: 'caslPermissions',
    defaults: {
        routePermissions: [],
        userPermissions: [],
        selectedRoutePermission: { _id: '', md5Checksum: '' },
        caslPermissionType: 'Routes',
        canOpenDialog: false,
        registeredAPIModels: [],
        registeredAPIRoutes: [],
        routePermissionForm: {
            model: {
                route: {
                    createdAt: '',
                    updatedAt: '',
                    path: '',
                    method: '',
                },
                subject: '',
                roles: [],
                comment: '',
            },
            dirty: false,
            status: '',
            errors: {},
        },
        userPermissionForm: {
            model: {
                role: '',
                action: '',
                subject: '',
            },
            dirty: false,
            status: '',
            errors: {},
        },
        rolesDropdown: {},
    },
})
@Injectable()
export class CaslPermissionsState {
    private _caslPermissionsService = inject(CaslPermissionsService);
    private _store = inject(Store);
    private _dropdownService = inject(DropdownOptionsService);
    @Action(SetCaslPermissionType)
    setCaslPermissionType(
        ctx: StateContext<CaslPermissionsStateModel>,
        { payload }: SetCaslPermissionType
    ) {
        ctx.patchState({ caslPermissionType: payload.caslPermissionType });
    }

    @Selector()
    static getCaslPermissionType(state: CaslPermissionsStateModel) {
        return state.caslPermissionType;
    }

    @Action(GetRoutePermissions)
    getRoutePermissions(ctx: StateContext<CaslPermissionsStateModel>, { payload }: GetRoutePermissions) {
        ctx.dispatch(new SetShowLoader(true));
        return this._caslPermissionsService.getRoutePermissions().pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error getting route permissions: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((routePermissions) => {
                ctx.dispatch(new OnSetSharedGridRowData({ reload: true, payload: routePermissions, gridUUID: payload.gridUUID }));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(GetRegisteredAPIModels)
    getRegisteredAPIModels(ctx: StateContext<CaslPermissionsStateModel>) {
        return this._caslPermissionsService.getRegisteredAPIModels().pipe(
            tap((registeredAPIModels: RegisteredAPIModel[]) => {
                ctx.patchState({ registeredAPIModels });
            })
        );
    }

    @Selector()
    static getRegisteredAPIModels(state: CaslPermissionsStateModel) {
        return state.registeredAPIModels.map((model) => model.modelName);
    }

    @Action(GetRegisteredAPIRoutes)
    getRegisteredAPIRoutes(ctx: StateContext<CaslPermissionsStateModel>) {
        return this._caslPermissionsService.getRegisteredAPIRoutes().pipe(
            tap((registeredAPIRoutes: RegisteredAPIRoute[]) => {
                ctx.patchState({ registeredAPIRoutes });
            })
        );
    }

    @Selector()
    static getRegisteredAPIRoutes(state: CaslPermissionsStateModel) {
        return state.registeredAPIRoutes;
    }

    @Action(SubmitRoutePermission)
    submitRoutePermission(ctx: StateContext<CaslPermissionsStateModel>) {
        const routePermissionForm = ctx.getState().routePermissionForm;

        let action;
        switch (routePermissionForm.model.route.method) {
            case 'GET':
                action = 'read';
                break;
            case 'POST':
                action = 'create';
                break;
            case 'PUT':
                action = 'update';
                break;
            case 'DELETE':
                action = 'delete';
                break;
            default:
                action = 'read'; // default action if method is not recognized
        }
        const routePermission = {
            route: routePermissionForm.model.route.path,
            method: routePermissionForm.model.route.method,
            action,
            subject: routePermissionForm.model.subject,
            roles: routePermissionForm.model.roles,
            comment: routePermissionForm.model.comment,
        };
        if (ctx.getState().selectedRoutePermission._id) {
            return this.updateRoutePermission(ctx, {
                payload: routePermission,
            });
        } else {
            return this.addRoutePermission(ctx, { payload: routePermission });
        }
    }

    addRoutePermission(ctx: StateContext<CaslPermissionsStateModel>, { payload }) {
        ctx.dispatch(new SetShowLoader(true));
        return this._caslPermissionsService.addRoutePermission(payload).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Route permission added successfully`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new GetRoutePermissions({ gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                ctx.dispatch(new OnCanCloseDialog(true));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    updateRoutePermission(ctx: StateContext<CaslPermissionsStateModel>, { payload }) {
        return this._caslPermissionsService.updateRoutePermission(ctx.getState().selectedRoutePermission, payload).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Route permission updated successfully`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new GetRoutePermissions({ gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                ctx.dispatch(new OnCanCloseDialog(true));
            })
        );
    }

    @Action(DeleteRoutePermission)
    deleteRoutePermission(ctx: StateContext<CaslPermissionsStateModel>, { payload }: DeleteRoutePermission) {
        ctx.dispatch(new SetShowLoader(true));
        return this._caslPermissionsService.deleteRoutePermission(payload).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new GetRoutePermissions({ gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(SetSelectedRoutePermission)
    setSelectedRoutePermission(
        ctx: StateContext<CaslPermissionsStateModel>,
        { payload }: SetSelectedRoutePermission
    ) {
        ctx.patchState({ selectedRoutePermission: payload });
    }

    @Action(GetRoleDropdownOptions)
    getRoleDropdownOptions(
        ctx: StateContext<CaslPermissionsStateModel>,
        { payload }: GetRoleDropdownOptions
    ) {
        return this._dropdownService.getDropdownsFilter(payload).pipe(
            tap((result: any) => {
                ctx.patchState({ rolesDropdown: result });
            })
        );
    }

    @Selector()
    static getRoles(state: CaslPermissionsStateModel) {
        return state.rolesDropdown;
    }
}